import React, { useState, useEffect } from 'react';
import { TrustedAdvisorService } from '../../lib/trustedAdvisorService';
import { 
  TrustedAdvisor, 
  AdvisorPermission, 
  DocumentType,
  PermissionType,
  ViewMode,
  BulkPermissionUpdateRequest
} from '../../types/trustedAdvisor';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { LoadingSpinner } from '../common/LoadingSpinner';
import * as Select from '@radix-ui/react-select';
import { 
  FileText, 
  Users, 
  Shield, 
  Save, 
  RefreshCw,
  Eye,
  Edit,
  Lock,
  Unlock,
  ChevronDown,
  Check,
  AlertCircle,
  Info
} from 'lucide-react';

interface PermissionMatrixProps {
  onRefresh: () => void;
  onAddAdvisor?: () => void;
}

interface Document {
  id: string;
  name: string;
  type: DocumentType;
}

const PermissionMatrix: React.FC<PermissionMatrixProps> = ({ onRefresh, onAddAdvisor }) => {
  const [advisors, setAdvisors] = useState<TrustedAdvisor[]>([]);
  const [permissions, setPermissions] = useState<AdvisorPermission[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [advisorsData, permissionsData, documentsData] = await Promise.all([
        TrustedAdvisorService.getTrustedAdvisors({ status: 'active' }),
        TrustedAdvisorService.getAdvisorPermissions(),
        TrustedAdvisorService.getDocumentsForPermissions()
      ]);
      
      setAdvisors(advisorsData);
      setPermissions(permissionsData);
      setDocuments(documentsData);
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const getPermissionForAdvisorAndDocument = (advisorId: string, documentId: string) => {
    return permissions.find(p => p.advisor_id === advisorId && p.document_id === documentId);
  };

  const handlePermissionChange = (advisorId: string, documentId: string, field: 'permission_type' | 'view_mode', value: string) => {
    const existingPermission = getPermissionForAdvisorAndDocument(advisorId, documentId);
    
    if (existingPermission) {
      // Update existing permission
      setPermissions(prev => prev.map(p => 
        p.id === existingPermission.id 
          ? { ...p, [field]: value }
          : p
      ));
    } else {
      // Create new permission
      const document = documents.find(d => d.id === documentId);
      const newPermission: AdvisorPermission = {
        id: `temp-${advisorId}-${documentId}`,
        advisor_id: advisorId,
        document_id: documentId,
        document_type: document?.type || 'will',
        permission_type: field === 'permission_type' ? value as PermissionType : 'do_not_share',
        view_mode: field === 'view_mode' ? value as ViewMode : 'view_only',
        unlocker_id: undefined,
        unlock_wait_period_days: 30,
        is_active: true,
        last_updated: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setPermissions(prev => [...prev, newPermission]);
    }
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      setError(null);

      // Group permissions by document
      const permissionsByDocument = permissions.reduce((acc, permission) => {
        if (!acc[permission.document_id]) {
          acc[permission.document_id] = [];
        }
        acc[permission.document_id].push(permission);
        return acc;
      }, {} as Record<string, AdvisorPermission[]>);

      // Save each document's permissions
      for (const [documentId, documentPermissions] of Object.entries(permissionsByDocument)) {
        const bulkRequest: BulkPermissionUpdateRequest = {
          document_id: documentId,
          advisor_permissions: documentPermissions.map(p => ({
            advisor_id: p.advisor_id,
            permission_type: p.permission_type,
            view_mode: p.view_mode,
            unlocker_id: p.unlocker_id,
            unlock_wait_period_days: p.unlock_wait_period_days
          }))
        };

        await TrustedAdvisorService.bulkUpdatePermissions(bulkRequest);
      }

      await loadData();
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const getPermissionTypeColor = (type: PermissionType) => {
    switch (type) {
      case 'share_now':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'share_after_death':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'do_not_share':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getViewModeColor = (mode: ViewMode) => {
    switch (mode) {
      case 'view_only':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'view_and_comment':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'view_and_edit':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'full_access':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPermissionTypeLabel = (type: PermissionType) => {
    switch (type) {
      case 'share_now':
        return 'Share Now';
      case 'share_after_death':
        return 'Share After Death';
      case 'do_not_share':
        return 'Do Not Share';
      default:
        return type;
    }
  };

  const getViewModeLabel = (mode: ViewMode) => {
    switch (mode) {
      case 'view_only':
        return 'View Only';
      case 'view_and_comment':
        return 'View & Comment';
      case 'view_and_edit':
        return 'View & Edit';
      case 'full_access':
        return 'Full Access';
      default:
        return mode;
    }
  };

  const getDocumentTypeLabel = (type: DocumentType) => {
    switch (type) {
      case 'will':
        return 'Will';
      case 'trust':
        return 'Trust';
      case 'power_of_attorney':
        return 'Power of Attorney';
      case 'health_directive':
        return 'Health Directive';
      case 'insurance':
        return 'Insurance';
      case 'financial_account':
        return 'Financial Account';
      case 'investment':
        return 'Investment';
      case 'retirement':
        return 'Retirement';
      case 'medical_record':
        return 'Medical Record';
      case 'appointment':
        return 'Appointment';
      case 'medication':
        return 'Medication';
      case 'pet_profile':
        return 'Pet Profile';
      case 'legacy_item':
        return 'Legacy Item';
      case 'digital_asset':
        return 'Digital Asset';
      default:
        return type;
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesType = selectedDocumentType === 'all' || doc.type === selectedDocumentType;
    const matchesSearch = searchTerm === '' || 
      doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getDocumentTypeLabel(doc.type).toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <AlertCircle className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Error Loading Permissions</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={loadData} variant="primary">
            Try Again
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Document Permissions</h2>
          <p className="text-gray-600 mt-1">
            Configure which trusted advisors can access your documents and when
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button onClick={loadData} variant="outline" className="flex items-center">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={handleSaveAll} 
            variant="primary" 
            className="flex items-center"
            disabled={saving || !hasChanges}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Type
            </label>
            <Select.Root value={selectedDocumentType} onValueChange={(value) => setSelectedDocumentType(value as DocumentType | 'all')}>
              <Select.Trigger className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <Select.Value placeholder="Select document type" />
                <Select.Icon>
                  <ChevronDown className="w-4 h-4" />
                </Select.Icon>
              </Select.Trigger>
              <Select.Portal>
                <Select.Content className="bg-white border border-gray-300 rounded-md shadow-lg">
                  <Select.Viewport className="p-1">
                    <Select.Item value="all" className="flex items-center px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                      <Select.ItemText>All Document Types</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="w-4 h-4 ml-2" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    <Select.Item value="will" className="flex items-center px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                      <Select.ItemText>Wills</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="w-4 h-4 ml-2" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    <Select.Item value="trust" className="flex items-center px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                      <Select.ItemText>Trusts</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="w-4 h-4 ml-2" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    <Select.Item value="power_of_attorney" className="flex items-center px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                      <Select.ItemText>Power of Attorney</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="w-4 h-4 ml-2" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    <Select.Item value="health_directive" className="flex items-center px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                      <Select.ItemText>Health Directives</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="w-4 h-4 ml-2" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    <Select.Item value="insurance" className="flex items-center px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                      <Select.ItemText>Insurance</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="w-4 h-4 ml-2" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    <Select.Item value="financial_account" className="flex items-center px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                      <Select.ItemText>Financial Accounts</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="w-4 h-4 ml-2" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    <Select.Item value="investment" className="flex items-center px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                      <Select.ItemText>Investments</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="w-4 h-4 ml-2" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    <Select.Item value="retirement" className="flex items-center px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                      <Select.ItemText>Retirement</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="w-4 h-4 ml-2" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    <Select.Item value="medical_record" className="flex items-center px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                      <Select.ItemText>Medical Records</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="w-4 h-4 ml-2" />
                      </Select.ItemIndicator>
                    </Select.Item>
                    <Select.Item value="digital_asset" className="flex items-center px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                      <Select.ItemText>Digital Assets</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check className="w-4 h-4 ml-2" />
                      </Select.ItemIndicator>
                    </Select.Item>
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Documents
            </label>
            <input
              type="text"
              placeholder="Search by name or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{advisors.length}</span> trusted advisors •{' '}
              <span className="font-medium">{filteredDocuments.length}</span> documents
            </div>
          </div>
        </div>
      </Card>

      {/* Permission Matrix */}
      {advisors.length === 0 ? (
        <Card className="p-6">
          <div className="text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Trusted Advisors</h3>
            <p className="text-gray-600 mb-4">
              You need to add trusted advisors before you can configure document permissions.
            </p>
            <Button variant="primary" onClick={onAddAdvisor}>
              Add Trusted Advisor
            </Button>
          </div>
        </Card>
      ) : filteredDocuments.length === 0 ? (
        <Card className="p-6">
          <div className="text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Documents Found</h3>
            <p className="text-gray-600">
              {searchTerm || selectedDocumentType !== 'all' 
                ? 'No documents match your current filters. Try adjusting your search or document type filter.'
                : 'No documents found. Add documents to your account to configure permissions.'
              }
            </p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document
                  </th>
                  {advisors.map(advisor => (
                    <th key={advisor.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-2" />
                        {advisor.first_name} {advisor.last_name}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDocuments.map(document => (
                  <tr key={document.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {document.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {getDocumentTypeLabel(document.type)}
                          </div>
                        </div>
                      </div>
                    </td>
                    {advisors.map(advisor => {
                      const permission = getPermissionForAdvisorAndDocument(advisor.id, document.id);
                      const permissionType = permission?.permission_type || 'do_not_share';
                      const viewMode = permission?.view_mode || 'view_only';

                      return (
                        <td key={advisor.id} className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            {/* Permission Type */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Access
                              </label>
                              <Select.Root 
                                value={permissionType} 
                                onValueChange={(value) => handlePermissionChange(advisor.id, document.id, 'permission_type', value)}
                              >
                                <Select.Trigger className="w-full flex items-center justify-between px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500">
                                  <Select.Value />
                                  <Select.Icon>
                                    <ChevronDown className="w-3 h-3" />
                                  </Select.Icon>
                                </Select.Trigger>
                                <Select.Portal>
                                  <Select.Content className="bg-white border border-gray-300 rounded shadow-lg">
                                    <Select.Viewport className="p-1">
                                      <Select.Item value="do_not_share" className="flex items-center px-2 py-1 text-xs hover:bg-gray-100 cursor-pointer">
                                        <Select.ItemText>Do Not Share</Select.ItemText>
                                        <Select.ItemIndicator>
                                          <Check className="w-3 h-3 ml-1" />
                                        </Select.ItemIndicator>
                                      </Select.Item>
                                      <Select.Item value="share_now" className="flex items-center px-2 py-1 text-xs hover:bg-gray-100 cursor-pointer">
                                        <Select.ItemText>Share Now</Select.ItemText>
                                        <Select.ItemIndicator>
                                          <Check className="w-3 h-3 ml-1" />
                                        </Select.ItemIndicator>
                                      </Select.Item>
                                      <Select.Item value="share_after_death" className="flex items-center px-2 py-1 text-xs hover:bg-gray-100 cursor-pointer">
                                        <Select.ItemText>Share After Death</Select.ItemText>
                                        <Select.ItemIndicator>
                                          <Check className="w-3 h-3 ml-1" />
                                        </Select.ItemIndicator>
                                      </Select.Item>
                                    </Select.Viewport>
                                  </Select.Content>
                                </Select.Portal>
                              </Select.Root>
                            </div>

                            {/* View Mode */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Level
                              </label>
                              <Select.Root 
                                value={viewMode} 
                                onValueChange={(value) => handlePermissionChange(advisor.id, document.id, 'view_mode', value)}
                              >
                                <Select.Trigger className="w-full flex items-center justify-between px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500">
                                  <Select.Value />
                                  <Select.Icon>
                                    <ChevronDown className="w-3 h-3" />
                                  </Select.Icon>
                                </Select.Trigger>
                                <Select.Portal>
                                  <Select.Content className="bg-white border border-gray-300 rounded shadow-lg">
                                    <Select.Viewport className="p-1">
                                      <Select.Item value="view_only" className="flex items-center px-2 py-1 text-xs hover:bg-gray-100 cursor-pointer">
                                        <Select.ItemText>View Only</Select.ItemText>
                                        <Select.ItemIndicator>
                                          <Check className="w-3 h-3 ml-1" />
                                        </Select.ItemIndicator>
                                      </Select.Item>
                                      <Select.Item value="view_and_comment" className="flex items-center px-2 py-1 text-xs hover:bg-gray-100 cursor-pointer">
                                        <Select.ItemText>View & Comment</Select.ItemText>
                                        <Select.ItemIndicator>
                                          <Check className="w-3 h-3 ml-1" />
                                        </Select.ItemIndicator>
                                      </Select.Item>
                                      <Select.Item value="view_and_edit" className="flex items-center px-2 py-1 text-xs hover:bg-gray-100 cursor-pointer">
                                        <Select.ItemText>View & Edit</Select.ItemText>
                                        <Select.ItemIndicator>
                                          <Check className="w-3 h-3 ml-1" />
                                        </Select.ItemIndicator>
                                      </Select.Item>
                                      <Select.Item value="full_access" className="flex items-center px-2 py-1 text-xs hover:bg-gray-100 cursor-pointer">
                                        <Select.ItemText>Full Access</Select.ItemText>
                                        <Select.ItemIndicator>
                                          <Check className="w-3 h-3 ml-1" />
                                        </Select.ItemIndicator>
                                      </Select.Item>
                                    </Select.Viewport>
                                  </Select.Content>
                                </Select.Portal>
                              </Select.Root>
                            </div>

                            {/* Status Badge */}
                            <div className="flex space-x-1">
                              <Badge 
                                variant={permissionType === 'do_not_share' ? 'secondary' : 'primary'}
                                className="text-xs"
                              >
                                {getPermissionTypeLabel(permissionType)}
                              </Badge>
                              <Badge 
                                variant="outline"
                                className="text-xs"
                              >
                                {getViewModeLabel(viewMode)}
                              </Badge>
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Legend */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Permission Types</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-2">Access Levels</h4>
            <div className="space-y-2">
              <div className="flex items-center">
                <Badge variant="secondary" className="mr-2">Do Not Share</Badge>
                <span className="text-sm text-gray-600">No access to this document</span>
              </div>
              <div className="flex items-center">
                <Badge variant="primary" className="mr-2">Share Now</Badge>
                <span className="text-sm text-gray-600">Immediate access granted</span>
              </div>
              <div className="flex items-center">
                <Badge variant="primary" className="mr-2">Share After Death</Badge>
                <span className="text-sm text-gray-600">Access after death verification</span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-2">Permission Levels</h4>
            <div className="space-y-2">
              <div className="flex items-center">
                <Badge variant="outline" className="mr-2">View Only</Badge>
                <span className="text-sm text-gray-600">Can view but not modify</span>
              </div>
              <div className="flex items-center">
                <Badge variant="outline" className="mr-2">View & Comment</Badge>
                <span className="text-sm text-gray-600">Can view and add comments</span>
              </div>
              <div className="flex items-center">
                <Badge variant="outline" className="mr-2">View & Edit</Badge>
                <span className="text-sm text-gray-600">Can view and edit content</span>
              </div>
              <div className="flex items-center">
                <Badge variant="outline" className="mr-2">Full Access</Badge>
                <span className="text-sm text-gray-600">Complete control over document</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PermissionMatrix; 